import { CustomDatePicker, DateBy } from '../../../common/datepicker/types';
import { PromptWithCategory } from '../types';
import { isSweepDefaultPrompt, SWEEP_AUTHOR_ID } from '@server/ai-prompts.config';
import { UNCATEGORIZED_FILTER_ID } from '../usePromptGroupState';
import { AdvancedFilterItem } from '../../../common/filters/advanced-filter/AdvancedFilter';
import { UserInfo } from '../../../../types/UserInfoTypes';

interface FilterPromptsParams {
  prompts: PromptWithCategory[];
  searchTerm: string;
  selectedCategoryFilters: string[];
  selectedUserFilters: string[];
  dateFilter: CustomDatePicker | null;
}

export const filterPrompts = ({
  prompts,
  searchTerm,
  selectedCategoryFilters,
  selectedUserFilters,
  dateFilter,
}: FilterPromptsParams): PromptWithCategory[] => {
  let filtered = prompts;

  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (prompt) =>
        prompt.name.toLowerCase().includes(lowerSearch) ||
        prompt.promptText.toLowerCase().includes(lowerSearch) ||
        prompt.categoryName.toLowerCase().includes(lowerSearch),
    );
  }

  if (selectedCategoryFilters.length > 0) {
    filtered = filtered.filter((prompt) => {
      if (selectedCategoryFilters.includes(UNCATEGORIZED_FILTER_ID) && !prompt.promptGroupId) {
        return true;
      }
      return selectedCategoryFilters.includes(prompt.categoryId);
    });
  }

  if (selectedUserFilters.length > 0) {
    filtered = filtered.filter((prompt) => {
      if (prompt.updatedById) {
        return selectedUserFilters.includes(prompt.updatedById);
      }
      const isSweepPrompt = isSweepDefaultPrompt(
        prompt.name,
        prompt.categoryName,
        prompt.updatedById,
      );
      if (isSweepPrompt) {
        return selectedUserFilters.includes(SWEEP_AUTHOR_ID);
      }
      return selectedUserFilters.includes('__blank__');
    });
  }

  if (dateFilter) {
    const fromDate = dateFilter[DateBy.From] ? new Date(dateFilter[DateBy.From]) : null;
    const toDate = dateFilter[DateBy.To] ? new Date(dateFilter[DateBy.To]) : null;

    filtered = filtered.filter((prompt) => {
      const promptDate = new Date(prompt.updatedAt);
      if (fromDate && promptDate < fromDate) return false;
      if (toDate && promptDate > toDate) return false;
      return true;
    });
  }

  return filtered;
};

export const filterPromptsByDate = (
  prompts: PromptWithCategory[],
  dateFilter: CustomDatePicker | null,
): PromptWithCategory[] => {
  if (!dateFilter) return prompts;

  const fromDate = dateFilter[DateBy.From] ? new Date(dateFilter[DateBy.From]) : null;
  const toDate = dateFilter[DateBy.To] ? new Date(dateFilter[DateBy.To]) : null;

  return prompts.filter((prompt) => {
    const promptDate = new Date(prompt.updatedAt);
    if (fromDate && promptDate < fromDate) return false;
    if (toDate && promptDate > toDate) return false;
    return true;
  });
};

export const getUserFilterItems = (
  prompts: PromptWithCategory[],
  accountUsers: UserInfo[] | undefined,
): AdvancedFilterItem[] => {
  const userIds = new Set(prompts.map((p) => p.updatedById).filter(Boolean));
  const hasSweepPrompts = prompts.some((p) =>
    isSweepDefaultPrompt(p.name, p.categoryName, p.updatedById),
  );
  const hasBlankPrompts = prompts.some(
    (p) => !p.updatedById && !isSweepDefaultPrompt(p.name, p.categoryName, p.updatedById),
  );

  const userItems: AdvancedFilterItem[] = (accountUsers || [])
    .filter((user) => user.id && userIds.has(user.id))
    .map((user) => ({
      label: user.name || user.email || 'Unknown',
      value: user.id as string,
    }));

  if (hasSweepPrompts) {
    userItems.push({ label: 'Sweep', value: SWEEP_AUTHOR_ID });
  }

  userItems.sort((a, b) => a.label.localeCompare(b.label));

  if (hasBlankPrompts) {
    userItems.unshift({ label: '(Blank)', value: '__blank__' });
  }

  return userItems;
};

export const promptTableColumns = [
  {
    field: 'name',
    headerName: 'Prompt Name',
    minWidth: '210px',
  },
  {
    field: 'categoryName',
    headerName: 'Category',
    minWidth: '200px',
  },
  {
    field: 'updatedByName',
    headerName: 'Last modified by',
  },
  {
    field: 'updatedAt',
    headerName: 'Last modified',
  },
];

interface PromptsEmptyStateParams {
  hasPrompts: boolean;
  hasActiveFilters: boolean;
}

export const getPromptsEmptyState = ({ hasPrompts, hasActiveFilters }: PromptsEmptyStateParams) => {
  if (!hasPrompts) {
    return {
      title: 'The category is empty.',
      description: 'Add a prompt to get started.',
    };
  }

  if (hasActiveFilters) {
    return {
      title: 'No prompts found.',
      description: 'Try a different search or add a new prompt to get started.',
    };
  }

  return {
    title: 'No prompts found.',
    description: 'Try a different search or add a new prompt to get started.',
  };
};
